import {
  EducationalOrganizationStructuredData,
  EducationalOccupationalStructuredData,
} from "design-system/components/primitives/head/head";
import { documentToString } from "frontend/contentful/lib/document-to-string";
import {
  LandingPageEntry,
  ProgramPageEntry,
} from "frontend/contentful/schema/pages";
import { SiteEntry } from "frontend/contentful/schema/universal";
import { useContentful } from "frontend/hooks/use-contentful";

export function getProvider(hasAddress?: boolean) {
  return {
    "@type": "CollegeOrUniversity",
    "@id": "https://www.hbs.edu/#organization",
    name: "Harvard Business School",
    url: "https://www.hbs.edu/",
    ...(hasAddress
      ? {
          address: {
            "@type": "PostalAddress",
            streetAddress: "Soldiers Field",
            addressLocality: "Boston",
            addressRegion: "MA",
            postalCode: "02163",
            addressCountry: "US",
          },
        }
      : {}),
  };
}

export function getOrgGlobalData(
  entry: ProgramPageEntry | LandingPageEntry,
  site: SiteEntry | undefined,
  hasCourse: boolean | undefined = false,
  hasSameAs: boolean | undefined = false,
  hasAddress: boolean | undefined = false,
  contentful: {
    getEntry: ReturnType<typeof useContentful>["getEntry"];
    getEntryUrl: ReturnType<typeof useContentful>["getEntryUrl"];
  },
): EducationalOccupationalStructuredData {
  const { getEntry, getEntryUrl } = contentful;
  const relativeUrl = getEntryUrl(entry) || entry.fields.url;
  const domain =
    site?.fields.domain === "exed" ? "www.exed" : site?.fields.domain || "www";
  const url = relativeUrl?.startsWith("http")
    ? relativeUrl
    : `https://${domain}.hbs.edu${relativeUrl ?? ""}`;
  const provider = getProvider(
    hasAddress,
  ) as EducationalOccupationalStructuredData["provider"];

  const seoEntry = entry.fields.seo ? getEntry(entry.fields.seo) : undefined;
  const description = seoEntry?.fields.description
    ? seoEntry?.fields.description
    : typeof entry.fields.description === "string"
      ? entry.fields.description
      : entry.fields.description
        ? documentToString(entry.fields.description)
        : undefined;

  // If we open this up to other sites other than Online, we need to re-review and adjust settings to be more dynamic as needed
  const orgDataProps: EducationalOccupationalStructuredData = {
    "@type": "EducationalOccupationalProgram",
    "@id": url + "#program",
    name: entry.fields.title,
    description,
    url,
    provider,
    programType: site?.fields.title,
  };

  if (hasCourse) {
    orgDataProps.hasCourse = [
      {
        "@type": "Course",
        name: entry.fields.title,
        description: description,
        url: url,
        provider,
      },
    ];
  }

  if (hasSameAs && site?.fields.footer) {
    const footer = getEntry(site.fields.footer);
    if (footer?.fields.organization) {
      const organization = getEntry(footer.fields.organization);
      organization?.fields.socialMediaLink?.forEach((link) => {
        const linkEntry = getEntry(link);
        const linkUrl = linkEntry?.fields.url;
        if (linkUrl && orgDataProps.sameAs) {
          orgDataProps.sameAs.push(linkUrl);
        } else if (linkUrl) {
          orgDataProps.sameAs = [linkUrl];
        }
      });
    }
  }

  return orgDataProps;
}

export function getOrgStructuredData(
  site?: SiteEntry,
  full?: boolean,
): EducationalOrganizationStructuredData {
  const { getEntry } = useContentful();

  const domain =
    site?.fields.domain === "exed" ? "www.exed" : site?.fields.domain || "www";
  const orgId = `https://${domain}.hbs.edu${site?.fields.slug}`;

  // If we open this up to other sites other then Online, we need to re-review and adjust settings to be more dynamic as needed
  const orgDataProps: EducationalOrganizationStructuredData = {
    "@id": orgId,
    "@type": "EducationalOrganization",
    name: `Harvard Business School ${site?.fields.title}`,
    url: `https://${domain}.hbs.edu${site?.fields.slug}`,
    address: {
      "@type": "PostalAddress",
      streetAddress: "Soldiers Field",
      addressLocality: "Boston",
      addressRegion: "MA",
      postalCode: "02163",
      addressCountry: "US",
    },
  };

  if (site?.fields.footer) {
    const footer = getEntry(site.fields.footer);
    if (footer?.fields.organization) {
      const organization = getEntry(footer.fields.organization);
      organization?.fields.socialMediaLink?.forEach((link) => {
        const linkEntry = getEntry(link);
        const url = linkEntry?.fields.url;
        if (url && orgDataProps.sameAs) {
          orgDataProps.sameAs.push(url);
        } else if (url) {
          orgDataProps.sameAs = [url];
        }
      });
    }
  }

  return full ? orgDataProps : { "@id": orgId };
}
